/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import * as Msdyn365 from '@msdyn365-commerce/core';
import { INodeProps } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

/**
 * ILinksData: Interface for
 * Additional content.
 */
interface ILinksData {
    linkText?: string;
    linkUrl: Msdyn365.ILinkData;
    ariaLabel?: string;
    isOpenInNewTab?: boolean;
}

/**
 * IAdditionalContentData: Interface for
 * Active content.
 */
interface IAdditionalContentData {
    heading?: string;
    subtext?: string;
    links?: ILinksData[];
}

/**
 * IActiveImageAdditionalContentProps: Interface for
 * Active Image Additional Content Component props.
 */
export interface IActiveImageAdditionalContentProps {
    requestContext: Msdyn365.IRequestContext;
    additionalContent: IAdditionalContentData | undefined;
    handleAdditionalTextChange(event: Msdyn365.ContentEditableEvent): void;
    handleAdditionalParagraphChange(event: Msdyn365.ContentEditableEvent): void;
    handleAdditionalLinkTextChange(linkIndex: number, event: Msdyn365.ContentEditableEvent): void;
}

/**
 * IActiveImageAdditionalContentViewProps: Interface for
 * Active Image Additional Content view props.
 */
export interface IActiveImageAdditionalContentViewProps {
    additionalContentNode: INodeProps;
    heading: React.ReactNode;
    text: React.ReactNode;
    links: React.ReactNode;
    additionalContentContainer: INodeProps;
    additionalContentItemLinks: INodeProps;
}

/**
 * Renders additional content heading.
 * @param additionalContentHeading - Additional content heading.
 * @param props - Active Image Additional content component props.
 * @returns Heading Node.
 */
const renderHeading = (additionalContentHeading: string, props: IActiveImageAdditionalContentProps) => {
    const requestContext = props.requestContext;
    return (
        <Msdyn365.Text
            text={additionalContentHeading}
            tag='p'
            className='msc-active-image__details__additional-content__text'
            editProps={{
                /**
                 * On edit event for heading text change.
                 * @param event - Change event.
                 */
                onEdit: (event) => {
                    props.handleAdditionalTextChange(event);
                }, requestContext
            }}
        />);
};

/**
 * Renders additional content heading.
 * @param additionalContentParagraphText - Additional content paragraph text.
 * @param props - Active Image Additional content component props.
 * @returns Paragraph Node.
 */
const renderParagraph = (additionalContentParagraphText: string, props: IActiveImageAdditionalContentProps) => {
    const requestContext = props.requestContext;
    return (
        <Msdyn365.Text
            text={additionalContentParagraphText}
            tag='p'
            className='msc-active-image__details__additional-content__paragraph'
            editProps={{
                /**
                 * On edit event for heading text change.
                 * @param event - Change event.
                 */
                onEdit: (event) => {
                    props.handleAdditionalTextChange(event);
                }, requestContext
            }}
        />);
};

/**
 * Renders additional content heading.
 * @param ctaLink - Additional content link.
 * @param props - Active Image Additional content component props.
 * @param linkIndex - Additional content link index.
 * @returns Link Node.
 */
const renderLinks = (ctaLink: ILinksData, props: IActiveImageAdditionalContentProps, linkIndex: number) => {
    const editableLink: Msdyn365.ILinksData = {
        ariaLabel: ctaLink.ariaLabel,
        className: 'msc-active-image__details__additional-content__cta__links',
        linkText: ctaLink.linkText,
        linkUrl: ctaLink.linkUrl.destinationUrl,
        openInNewTab: ctaLink.isOpenInNewTab,
        role: 'button'
    };
    const requestContext = props.requestContext;
    return (
        <Msdyn365.Link
            key={linkIndex}
            link={editableLink}
            editProps={{
                /**
                 * Handle additional Link Text change.
                 * @param event - The keyboard event.
                 */
                onTextChange: (event) => {
                    props.handleAdditionalLinkTextChange(linkIndex, event);
                },
                requestContext
            }}
        />
    );
};

/**
 * Create node for each additonal content item.
 * @param item - Active Image additonal content item props.
 * @param props - Active Image Additional content component props.
 * @returns IActiveImageAdditionalContentItemViewProps.
 */
const assembleNode = (
    item: IAdditionalContentData,
    props: IActiveImageAdditionalContentProps
): IActiveImageAdditionalContentViewProps => {
    const headingNode = item.heading && renderHeading(item.heading, props);
    const paragraphNode = item.subtext && renderParagraph(item.subtext, props);
    const linksNode = item.links?.map((ctaLink: ILinksData, index: number) => {
        return renderLinks(ctaLink, props, index);
    });

    return {
        additionalContentNode: { className: 'msc-active-image__details__additional-content' },
        heading: headingNode,
        text: paragraphNode,
        links: linksNode,
        additionalContentContainer: { className: 'msc-active-image__details__additional-content__container' },
        additionalContentItemLinks: { className: 'msc-active-image__details__additional__content__cta' }
    };
};

/**
 * ActiveImageAdditionalContent component.
 * @param props - Active Image Additional content component props.
 * @returns Active Image Additional content view props.
 */
export const activeImageAdditionalContent = (props: IActiveImageAdditionalContentProps): IActiveImageAdditionalContentViewProps | undefined => {
    const { additionalContent } = props;
    if (!additionalContent) {
        return undefined;
    }

    return assembleNode(additionalContent, props);
};

export default activeImageAdditionalContent;

